/**
 * RRULE utilities — shared logic with crewbit-mobile/utils/recurrence.ts.
 */

function parseRule(rule: string) {
  const parts = rule.split(';');
  const map = new Map<string, string>();
  for (const p of parts) {
    const [k, v] = p.split('=');
    if (k && v) map.set(k, v);
  }
  return {
    freq: map.get('FREQ') ?? 'WEEKLY',
    interval: parseInt(map.get('INTERVAL') ?? '1', 10),
    byDay: map.get('BYDAY')?.split(','),
    byMonthDay: map.get('BYMONTHDAY') ? parseInt(map.get('BYMONTHDAY')!, 10) : undefined,
  };
}

function getOccurrences(startAt: Date, rule: string, rangeStart: Date, rangeEnd: Date): Date[] {
  const { freq, interval } = parseRule(rule);
  const dates: Date[] = [];
  const cursor = new Date(startAt);
  for (let i = 0; i < 365 && cursor <= rangeEnd; i++) {
    if (cursor >= rangeStart && cursor <= rangeEnd) dates.push(new Date(cursor));
    switch (freq) {
      case 'DAILY': cursor.setDate(cursor.getDate() + interval); break;
      case 'WEEKLY': cursor.setDate(cursor.getDate() + 7 * interval); break;
      case 'MONTHLY': cursor.setMonth(cursor.getMonth() + interval); break;
      default: cursor.setDate(cursor.getDate() + 7 * interval);
    }
  }
  return dates;
}

/** Generate ISO date strings from an RRULE starting at startDate for daysAhead days. */
export function generateDatesFromRRule(rrule: string, startDate: string, daysAhead: number): string[] {
  const start = new Date(startDate + 'T12:00:00');
  const end = new Date(start);
  end.setDate(end.getDate() + daysAhead);
  return getOccurrences(start, rrule, start, end)
    .filter(d => d.toISOString().split('T')[0] !== startDate)
    .map(d => d.toISOString().split('T')[0]);
}

/** Build RRULE string from user selection. */
export function buildRRule(pattern: string, date: string): string {
  const d = new Date(date + 'T12:00:00');
  const dayNames = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
  const dayOfWeek = dayNames[d.getDay()];
  const dayOfMonth = d.getDate();

  switch (pattern) {
    case 'daily': return 'FREQ=DAILY';
    case 'weekly': return `FREQ=WEEKLY;BYDAY=${dayOfWeek}`;
    case 'weekdays': return 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR';
    case 'biweekly': return `FREQ=WEEKLY;INTERVAL=2;BYDAY=${dayOfWeek}`;
    case 'monthly': return `FREQ=MONTHLY;BYMONTHDAY=${dayOfMonth}`;
    default: return '';
  }
}
